import * as React from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslate } from 'react-admin';
import { isAcknowledgeTaskTemplate } from './acknowledgeTaskTemplate';
import { buildMetaScriptForTaskTemplate, getGetAssetsFilterField } from './processTemplateTransforms';
import { resolveScheduleTaskTemplateId } from './scheduleTaskComparison';
import { ScheduleTaskForm, TaskTemplateRecord } from './types';
import { XgacFilterEditor } from './XgacFilterEditor';
import { ScheduleTaskMetaDataEditor } from './ScheduleTaskMetaDataEditor';
import { NumericInputField, NumericTextField } from './NumericInputField';

type ScheduleTaskEditorProps = {
  task: ScheduleTaskForm;
  index: number;
  taskTemplates: TaskTemplateRecord[];
  expanded: boolean;
  isSameAsPrevious: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onActivate: () => void;
  onChange: (task: ScheduleTaskForm) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  canRemove?: boolean;
  disabled?: boolean;
};

export const ScheduleTaskEditor = ({
  task,
  index,
  taskTemplates,
  expanded,
  isSameAsPrevious,
  onExpandedChange,
  onActivate,
  onChange,
  onRemove,
  onDuplicate,
  canRemove = true,
  disabled = false,
}: ScheduleTaskEditorProps) => {

  const translate = useTranslate();

  const taskTemplateId = resolveScheduleTaskTemplateId(task.taskTemplate);
  const taskTemplateRecord = taskTemplates.find((template) => template.id === taskTemplateId) || null;
  const filterField = getGetAssetsFilterField(taskTemplateRecord);
  const metaScriptOptions = taskTemplateRecord?.inputMeta?.metaScripts || [];
  const delaySeconds = task.delaySeconds ?? (task.delay / 1000);
  const collapsedSummary = taskTemplateRecord?.templateName
    || translate('resources.process-templates.text.step_not_configured', { _: 'Not configured' });

  const updateTask = (partial: Partial<ScheduleTaskForm>) => {

    onChange({ ...task, ...partial });

  };

  const handleTaskTemplateChange = (newId: string | null) => {

    if (!newId) {

      updateTask({
        taskTemplate: null, metaScript: null, ifScript: null, metaData: null,
      });
      return;

    }

    const selected = taskTemplates.find((template) => template.id === newId);
    if (!selected) return;

    updateTask({
      taskTemplate: newId,
      metaScript: buildMetaScriptForTaskTemplate(selected),
      ifScript: null,
      metaData: null,
    });

  };

  const handleMetaScriptChange = (name: string) => {

    const selected = metaScriptOptions.find((script) => script.name === name);
    if (!selected) return;

    const metaScript = buildMetaScriptForTaskTemplate({
      inputMeta: { metaScripts: [selected] },
    });
    updateTask({ metaScript: metaScript as ScheduleTaskForm['metaScript'] });

  };

  const handleIfScriptChange = (name: string) => {

    if (!name) {

      updateTask({ ifScript: null });
      return;

    }
    updateTask({
      ifScript: {
        name: 'checkConfirmations',
        meta: { requiredConfirmations: task.ifScript?.meta?.requiredConfirmations || '2' },
      },
    });

  };

  const handleSkipEscalationCheckChange = (checked: boolean) => {

    const next = { ...(task.metaData || {}) };

    if (checked) {

      next.skipEscalationCheck = true;

    } else {

      delete next.skipEscalationCheck;

    }

    updateTask({ metaData: Object.keys(next).length > 0 ? next : null });

  };

  const toggleExpanded = (event: React.MouseEvent) => {

    event.stopPropagation();
    onActivate();
    onExpandedChange(!expanded);

  };

  const handleHeaderClick = () => {

    onActivate();

  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
        }}
        onClick={handleHeaderClick}
      >
        <IconButton
          size="small"
          aria-label={expanded
            ? translate('ra.action.close', { _: 'Collapse' })
            : translate('ra.action.expand', { _: 'Expand' })}
          onClick={toggleExpanded}
        >
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6">
            {translate('resources.process-templates.text.step_number', { number: index + 1 })}
          </Typography>
          {!expanded && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5,
            }}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {collapsedSummary}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`${delaySeconds} ${translate('general.text.seconds')}`}
              </Typography>
              {isSameAsPrevious && (
                <Chip
                  size="small"
                  label={translate('resources.process-templates.text.same_as_previous_step', { _: 'Same as previous step' })}
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </Box>

        <Box onClick={(event) => event.stopPropagation()}>
          <IconButton
            size="small"
            onClick={onDuplicate}
            disabled={disabled}
            aria-label={translate('resources.process-templates.text.duplicate_step')}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={onRemove}
            disabled={disabled || !canRemove}
            aria-label={translate('resources.process-templates.text.remove_step')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <CardContent sx={{ pt: 0 }}>
          {isSameAsPrevious && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate('resources.process-templates.text.same_as_previous_step', { _: 'Same as previous step' })}
            </Typography>
          )}

          <Autocomplete
            key={`${task.id}-${taskTemplateId ?? 'none'}`}
            options={taskTemplates}
            disabled={disabled}
            getOptionLabel={(option) => option.templateName}
            value={taskTemplateRecord}
            onChange={(_, selected) => handleTaskTemplateChange(selected?.id || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('resources.process-templates.text.taskTemplate.id')}
                required
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {taskTemplateId && (
            <>
              <NumericInputField
                label={translate('resources.process-templates.fields.delay', { _: 'Delay' })}
                value={delaySeconds}
                disabled={disabled}
                onChange={(nextDelaySeconds) => updateTask({
                  delaySeconds: nextDelaySeconds,
                  delay: nextDelaySeconds * 1000,
                })}
                inputProps={{ min: 0, step: 1 }}
                sx={{ mt: 2, width: 200 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{translate('general.text.seconds')}</InputAdornment>,
                }}
              />

              <FormControl fullWidth sx={{ mt: 2 }} disabled={disabled}>
                <InputLabel>{translate('resources.process-templates.text.if_script')}</InputLabel>
                <Select
                  value={task.ifScript?.name || ''}
                  label={translate('resources.process-templates.text.if_script')}
                  onChange={(event) => handleIfScriptChange(event.target.value)}
                >
                  <MenuItem value="">{translate('resources.process-templates.text.always_execute')}</MenuItem>
                  <MenuItem value="checkConfirmations">checkConfirmations</MenuItem>
                </Select>
              </FormControl>

              {task.ifScript?.name === 'checkConfirmations' && (
                <NumericTextField
                  label={translate('resources.process-templates.fields.required_confirmations', { _: 'Required confirmations' })}
                  value={task.ifScript.meta?.requiredConfirmations || ''}
                  onChange={(requiredConfirmations) => updateTask({
                    ifScript: {
                      name: 'checkConfirmations',
                      meta: { requiredConfirmations },
                    },
                  })}
                  inputProps={{ min: 1 }}
                  disabled={disabled}
                  sx={{ mt: 2, width: 200 }}
                />
              )}

              <FormControl fullWidth sx={{ mt: 2 }} disabled={disabled}>
                <InputLabel>{translate('resources.process-templates.text.meta_script')}</InputLabel>
                <Select
                  value={task.metaScript?.name || ''}
                  label={translate('resources.process-templates.text.meta_script')}
                  onChange={(event) => handleMetaScriptChange(event.target.value)}
                  disabled={disabled || metaScriptOptions.length <= 1}
                >
                  {metaScriptOptions.map((script) => (
                    <MenuItem key={script.name} value={script.name}>
                      {script.label || script.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {task.metaScript?.name === 'getAssets' && (
                <Box sx={{ mt: 2 }}>
                  <XgacFilterEditor
                    value={task.metaScript.meta?.xgacFilter || {}}
                    filterField={filterField}
                    disabled={disabled}
                    onChange={(xgacFilter) => {

                      const metaScript = task.metaScript;
                      if (!metaScript) return;

                      updateTask({
                        metaScript: {
                          ...metaScript,
                          meta: {
                            ...metaScript.meta,
                            filter: {},
                            xgacFilter,
                          },
                        },
                      });

                    }}
                  />
                </Box>
              )}

              {isAcknowledgeTaskTemplate(taskTemplateRecord) && (
                <FormControl sx={{ mt: 2, display: 'block' }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={task.metaData?.skipEscalationCheck === true}
                        disabled={disabled}
                        onChange={(_, checked) => handleSkipEscalationCheckChange(checked)}
                      />
                    )}
                    label={translate('resources.process-templates.fields.skip_escalation_check', {
                      _: 'Skip escalation check',
                    })}
                  />
                  <FormHelperText>
                    {translate('resources.process-templates.text.skip_escalation_check_help', {
                      _: 'When enabled, acknowledge still runs even if the alarm was escalated to an alarm center.',
                    })}
                  </FormHelperText>
                </FormControl>
              )}

              <ScheduleTaskMetaDataEditor
                taskTemplate={taskTemplateRecord}
                value={task.metaData}
                disabled={disabled}
                onChange={(metaData) => updateTask({ metaData })}
              />
            </>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );

};
